import React, { useMemo, useRef, useState } from "react";
import { CloseOutlined } from "@ant-design/icons";
import { notification } from "antd";
import { useTranslation } from "react-i18next";
import styles from "./style.module.scss";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const RecipientCombobox = ({
  value = [],
  onChange,
  suggestions = [],
  memberCandidates = [],
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);

  const selected = useMemo(() => new Set(value.map((x) => x.toLowerCase())), [value]);

  const memberLabelByEmail = useMemo(() => {
    const map = new Map();
    (memberCandidates || []).forEach((member) => {
      const email = String(member?.email || "").trim().toLowerCase();
      if (!email) return;
      const name = String(member?.display_name || "").trim();
      map.set(email, name && name.toLowerCase() !== email ? `${name} (${email})` : email);
    });
    return map;
  }, [memberCandidates]);

  const filteredSuggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    const pool = suggestions.filter((email) => email && !selected.has(String(email).toLowerCase()));
    if (!q) return pool.slice(0, 8);
    return pool
      .filter((email) => {
        const normalized = String(email).toLowerCase();
        const label = memberLabelByEmail.get(normalized) || normalized;
        return normalized.includes(q) || label.toLowerCase().includes(q);
      })
      .slice(0, 8);
  }, [query, selected, suggestions, memberLabelByEmail]);

  const commitEmail = (raw) => {
    const trimmed = String(raw || "").trim().toLowerCase();
    if (!trimmed) return;
    if (!EMAIL_REGEX.test(trimmed)) {
      notification.error({ message: t("email_reports_page.invalidEmail") });
      return;
    }
    if (selected.has(trimmed)) {
      notification.warning({ message: t("email_reports_page.duplicateEmail") });
      return;
    }
    if (onChange) {
      onChange([...value, trimmed]);
    }
    setQuery("");
    setOpen(false);
  };

  const removeEmail = (addr) => {
    if (onChange) {
      onChange(value.filter((x) => x !== addr));
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === "," || event.key === "Tab") {
      if (query.trim()) {
        event.preventDefault();
        commitEmail(query);
      }
    } else if (event.key === "Backspace" && !query && value.length > 0) {
      if (onChange) {
        onChange(value.slice(0, -1));
      }
    } else if (event.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className={styles.recipientComboboxWrap}>
      <div
        className={`${styles.recipientCombobox} ${disabled ? styles.recipientComboboxDisabled : ""}`}
        onClick={() => {
          if (!disabled && inputRef.current) {
            inputRef.current.focus();
          }
        }}
      >
        {value.map((email) => (
          <span key={email} className={styles.recipientChip}>
            {email}
            {!disabled ? (
              <button
                type="button"
                className={styles.recipientChipRemove}
                aria-label={t("email_reports_page.removeRecipient", { email })}
                onClick={(event) => {
                  event.stopPropagation();
                  removeEmail(email);
                }}
              >
                <CloseOutlined />
              </button>
            ) : null}
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          className={styles.recipientInput}
          value={query}
          disabled={disabled}
          placeholder={value.length === 0 ? t("email_reports_page.recipientComboboxPlaceholder") : ""}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            window.setTimeout(() => setOpen(false), 120);
          }}
          onKeyDown={handleKeyDown}
        />
      </div>
      {!disabled && open && (filteredSuggestions.length > 0 || query.trim()) ? (
        <ul className={styles.recipientDropdown} role="listbox">
          {filteredSuggestions.map((email) => (
            <li key={email}>
              <button
                type="button"
                className={styles.recipientOption}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => commitEmail(email)}
              >
                {memberLabelByEmail.get(String(email).toLowerCase()) || email}
              </button>
            </li>
          ))}
          {query.trim() && EMAIL_REGEX.test(query.trim()) && !selected.has(query.trim().toLowerCase()) ? (
            <li>
              <button
                type="button"
                className={styles.recipientOption}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => commitEmail(query)}
              >
                {t("email_reports_page.addRecipientOption", { email: query.trim() })}
              </button>
            </li>
          ) : null}
        </ul>
      ) : null}
      <p className={styles.fieldHint}>{t("email_reports_page.recipientsExternalHint")}</p>
    </div>
  );
};

export default RecipientCombobox;
